import { DashboardTile } from '../../pages/Dashboard/Dashboard.types';
import { areObjectsEqual } from '../../utils/areObjectsEqual';
import { BlockadeTypeId, ExaminersList, Inferiors } from '../ExaminersStructure/ExaminersStructure.types';


export const findStructureElement = (id: string, structure: ExaminersList): ExaminersList | undefined => {
  if (structure.examiner && structure.examiner.id === id) {
    return structure;
  } else {
    let foundElement = undefined;
    const findingResult = structure.inferiors.find((inferior: Inferiors): boolean => {
      foundElement = findStructureElement(id, inferior);
      return !!foundElement;
    });
    if (findingResult) {
      return foundElement;
    } else {
      return undefined;
    }
  }
};

export const getBlockadeFilter = (blockadeFilterType: BlockadeTypeId, element: ExaminersList) => {
  switch (blockadeFilterType) {
    case BlockadeTypeId.Automatic:
      return element.examiner?.examinerPermission?.isBlocked === BlockadeTypeId.Automatic;
    case BlockadeTypeId.Manual:
      return element.examiner?.examinerPermission?.isBlocked === BlockadeTypeId.Manual;
    case BlockadeTypeId.Any:
      return (
        element.examiner?.examinerPermission?.isBlocked === BlockadeTypeId.Manual ||
        element.examiner?.examinerPermission?.isBlocked === BlockadeTypeId.Automatic
      );
    case BlockadeTypeId.None:
      return true;
    default:
      return true;
  }
};

const checkIsInputContainsEmployeeNumber = (element: ExaminersList, employeeNumber: string | null): boolean => {
  return employeeNumber
    ? element.examiner?.examinerPermission?.examinerNumber
      ? element.examiner?.examinerPermission.examinerNumber.includes(employeeNumber)
      : false
    : false;
};

export const isSearchResult = (element: ExaminersList, search: string) => {
  return (
    (element.examiner && element.examiner.firstName.toLowerCase().includes(search.toLowerCase())) ||
    (element.examiner && element.examiner.lastName.toLowerCase().includes(search.toLowerCase())) ||
    (element.examiner && checkIsInputContainsEmployeeNumber(element, search))
  );
};

export const getIdsFromAStructure = (data: ExaminersList): Array<string> => {
  let list: Array<string> = [];
  if (data !== null) {
    if (data.examiner !== null) list.push(data.examiner.id);
    if (data.inferiors.length > 0) {
      data.inferiors.forEach((el) => {
        const newList = getIdsFromAStructure(el);
        list = [...list, ...newList];
      });
    }
  }
  return list;
};

export const updateExaminerStructureWithSignalRInfo = (
  data: ExaminersList,
  userId: string,
  isOnline: boolean,
): ExaminersList => {
  let changed = false;

  let newExaminer = data.examiner;
  if (data.examiner?.id === userId && data.examiner.isOnline !== isOnline) {
    newExaminer = { ...data.examiner, isOnline };
    changed = true;
  }

  const newInferiors = data.inferiors.map((inferior) => {
    const updated = updateExaminerStructureWithSignalRInfo(inferior, userId, isOnline);
    if (updated !== inferior) changed = true;
    return updated;
  });

  if (changed) {
    return { ...data, examiner: newExaminer, inferiors: newInferiors };
  }

  return data;
};

export const updateExaminerStructureWithSignalRBlockadeInfo = (
  data: ExaminersList,
  examinerPermissionId: string,
  blockadeType: BlockadeTypeId | undefined,
  assessmentSessionId: string,
  assessmentSession: DashboardTile | null,
): ExaminersList => {
  const blockade = blockadeType || BlockadeTypeId.None;
  if (
    assessmentSessionId === assessmentSession?.assessmentSessionId &&
    examinerPermissionId === data.examiner?.examinerPermission?.id
  ) {
    const newParams = { isBlocked: blockade };
    const needsToBeRecreated = !areObjectsEqual(data.examiner.examinerPermission, newParams);

    if (needsToBeRecreated) {
      return {
        ...data,
        examiner: {
          ...data.examiner,
          examinerPermission: { ...data.examiner.examinerPermission, ...newParams },
        },
      };
    }
    return data;
  }

  let inferiorsAreRecreated = false;
  const newInferiors = data.inferiors.map((el) => {
    const newData = updateExaminerStructureWithSignalRBlockadeInfo(
      el,
      examinerPermissionId,
      blockadeType,
      assessmentSessionId,
      assessmentSession,
    );

    if (newData !== el) {
      inferiorsAreRecreated = true;
    }
    return newData;
  });

  if (inferiorsAreRecreated) {
    return {
      ...data,
      inferiors: newInferiors,
    };
  }

  return data;
};

export const updateExaminerStructureActiveExaminer = (
  data: ExaminersList,
  currentActiveExaminer: ExaminersList,
): ExaminersList => {
  let needsToBeRecreated = false;
  let newParams = {};
  newParams = { isActive: data.id === currentActiveExaminer.id };
  needsToBeRecreated = !areObjectsEqual(data, newParams);

  let inferiorsAreRecreated = false;
  const newInferiors = data?.inferiors.map((el) => {
    const newData = updateExaminerStructureActiveExaminer(el, currentActiveExaminer);

    if (newData !== el) {
      inferiorsAreRecreated = true;
    }
    return newData;
  });

  const inferiors = inferiorsAreRecreated ? newInferiors : data.inferiors;
  if (needsToBeRecreated || inferiorsAreRecreated) {
    return { ...data, ...newParams, inferiors };
  } else {
    return data;
  }
};
export const updateExaminerStructureClosedExaminer = (
  data: ExaminersList,
  examiner?: ExaminersList,
  isClosed?: boolean,
): ExaminersList => {
  let needsToBeRecreated = false;
  let newParams = {};

  if (!examiner || examiner.id === data.id) {
    newParams = { isClosed: isClosed === undefined ? !data.isClosed : isClosed };
    needsToBeRecreated = !areObjectsEqual(data, newParams);
  }

  let inferiorsAreRecreated = false;
  const newInferiors = data?.inferiors.map((el) => {
    const newData = updateExaminerStructureClosedExaminer(el, examiner, isClosed);
    if (newData !== el) {
      inferiorsAreRecreated = true;
    }
    return newData;
  });

  const inferiors = inferiorsAreRecreated ? newInferiors : data.inferiors;
  if (needsToBeRecreated || inferiorsAreRecreated) {
    return { ...data, ...newParams, inferiors };
  } else {
    return data;
  }
};
